"use client";

import { useState, useCallback } from "react";
import {
  Upload,
  FileText,
  Image,
  File,
  X,
  Loader2,
  User,
  Shield,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ExtractionResult } from "@/lib/text-extractor";
import { AnonymizationResult, buildDeanonymizationMap } from "@/lib/anonymizer";
import { matchDocumentToPatient, type PatientForMatch } from "@/lib/patient-declensions";
import { runIdentifyAndAnonymize, DOCUMENT_CONTEXT_MAX_LENGTH, type PatientResult } from "@/lib/identify-and-anonymize";

export type { PatientForMatch };

/** Supprime les mots consécutifs dupliqués (ex. "Camille Camille" → "Camille"). */
function collapseDuplicateWords(s: string): string {
  return s
    .trim()
    .split(/\s+/)
    .filter((word, i, arr) => word !== arr[i - 1])
    .join(" ")
    .trim();
}

/** Normalise nom/prénom issus de l'API : pas de doublon dans le prénom, le NOM ne doit pas finir par le prénom. */
function normalizePatientNames(nomRaw: string | undefined, prenomRaw: string | undefined): { lastName: string; firstName: string } {
  let firstName = collapseDuplicateWords((prenomRaw ?? "").trim());
  let lastName = (nomRaw ?? "").trim();
  if (!firstName) firstName = "Inconnu";
  if (!lastName) lastName = "Inconnu";
  const firstNameNorm = firstName.toUpperCase();
  const lastNameTrim = lastName.trimEnd();
  if (firstNameNorm && lastNameTrim.endsWith(" " + firstName)) {
    lastName = lastNameTrim.slice(0, -(firstName.length + 1)).trimEnd();
    if (!lastName) lastName = "Inconnu";
  } else if (firstNameNorm && lastNameTrim.toUpperCase().endsWith(" " + firstNameNorm)) {
    lastName = lastNameTrim.slice(0, -(firstName.length + 1)).trimEnd();
    if (!lastName) lastName = "Inconnu";
  }
  return { lastName, firstName };
}

interface PatientIdentification {
  results: PatientResult[];
}

interface PatientDocumentDropZoneProps {
  practitioners?: string[];
  existingPatients: PatientForMatch[];
  onPatientLinked?: (patientId: string, isNew: boolean) => void;
  onPatientsRefresh?: () => void;
  className?: string;
}

export function PatientDocumentDropZone({
  practitioners = [],
  existingPatients,
  onPatientLinked,
  onPatientsRefresh,
  className,
}: PatientDocumentDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [extractionResult, setExtractionResult] = useState<ExtractionResult | null>(null);
  const [identifiedPatient, setIdentifiedPatient] = useState<PatientIdentification | null>(null);
  const [anonymizationResult, setAnonymizationResult] = useState<AnonymizationResult | null>(null);
  const [matchResult, setMatchResult] = useState<{
    matchedPatientId: string | null;
    confidence: "high" | "medium" | "low";
    reason: string;
  } | null>(null);
  const [isLinking, setIsLinking] = useState(false);
  const [linkSuccess, setLinkSuccess] = useState<string | null>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const processFile = async (selectedFile: File) => {
    setFile(selectedFile);
    setIsProcessing(true);
    setError(null);
    setProgress("Lecture du fichier...");
    setMatchResult(null);
    setLinkSuccess(null);

    try {
      setProgress("Extraction et identification du patient…");
      const identifyResult = await runIdentifyAndAnonymize({
        file: selectedFile,
        practitioners,
        documentContextMaxLength: DOCUMENT_CONTEXT_MAX_LENGTH,
        includeOtherNamesInAnonymization: true,
      });

      if ("error" in identifyResult) {
        setProgress("");
        setError(identifyResult.error ?? null);
        setExtractionResult(identifyResult.extraction ?? null);
        setIsProcessing(false);
        return;
      }

      const { extraction: result, identifiedResults: resultsWithIds, anonymized } = identifyResult;
      setExtractionResult(result);
      setIdentifiedPatient({ results: resultsWithIds });
      setAnonymizationResult(anonymized);

      setProgress("Extraction date de naissance (document anonymisé)...");
      let docBirthDate: string | null = null;
      try {
        const birthRes = await fetch("/api/extract-birthdate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ anonymizedText: anonymized.anonymizedText }),
        });
        if (birthRes.ok) {
          const birthData = await birthRes.json();
          if (birthData.birthdate) {
            const [d, m, y] = birthData.birthdate.split("/");
            if (d && m && y) docBirthDate = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
          }
        }
      } catch {
        // ignore
      }

      setProgress("Recherche patient correspondant (local, sans IA)...");
      const docDeclensionValues = resultsWithIds.map((r) => r.value).filter(Boolean);
      const match = matchDocumentToPatient(
        docDeclensionValues,
        docBirthDate,
        existingPatients
      );
      setMatchResult(match);

      setProgress("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors du traitement");
      setProgress("");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) await processFile(droppedFile);
    },
    [existingPatients, practitioners]
  );

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) await processFile(selectedFile);
  };

  const handleLinkToPatient = async () => {
    if (!anonymizationResult?.anonymizedText || !identifiedPatient) return;

    setIsLinking(true);
    setError(null);
    setLinkSuccess(null);

    try {
      const extractRes = await fetch("/api/patients/extract-from-document", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ anonymizedText: anonymizationResult.anonymizedText }),
      });

      if (!extractRes.ok) throw new Error("Erreur extraction");
      const extracted = await extractRes.json();

      const nomRaw = identifiedPatient.results.find(
        (r) => r.format === "NOM" || r.format?.toUpperCase() === "NOM"
      )?.value?.trim();
      const prenomRaw = identifiedPatient.results.find(
        (r) =>
          r.format === "Prénom" ||
          r.format === "PRÉNOM" ||
          r.format?.toUpperCase() === "PRENOM"
      )?.value?.trim();
      const { lastName, firstName } = normalizePatientNames(nomRaw, prenomRaw);
      const declensions = identifiedPatient.results.map((r) => r.value).filter(Boolean);

      if (matchResult?.matchedPatientId) {
        const patientId = matchResult.matchedPatientId;
        const deanonymMap = buildDeanonymizationMap(anonymizationResult.replacements);
        if (extracted.notes) {
          const patchRes = await fetch(`/api/patients/${patientId}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              notes: extracted.notes,
              nameDeclensions: declensions,
              notesAnonymizationMap: Object.keys(deanonymMap).length > 0 ? deanonymMap : undefined,
            }),
          });
          if (!patchRes.ok) throw new Error("Erreur mise à jour patient");
        }
        for (const patho of extracted.pathologies || []) {
          await fetch(`/api/patients/${patientId}/pathologies`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: patho.name, description: patho.description ?? undefined }),
          });
        }
        const reportRes = await fetch(`/api/patients/${patientId}/reports`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: `Rapport importé - ${file?.name ?? new Date().toLocaleDateString("fr-FR")}`,
            content: anonymizationResult.anonymizedText.substring(0, 5000),
            summary: extracted.reportSummary ?? undefined,
            improvements: extracted.reportImprovements ?? undefined,
            actionsToCome: extracted.reportActionsToCome ?? undefined,
            reportDate: new Date().toISOString().split("T")[0],
            anonymizationMap: Object.keys(deanonymMap).length > 0 ? deanonymMap : undefined,
          }),
        });
        if (!reportRes.ok) {
          const errData = await reportRes.json().catch(() => ({}));
          throw new Error(errData?.error ?? "Erreur création du rapport");
        }
        if (file) {
          const created = await reportRes.json();
          const formData = new FormData();
          formData.append("file", file);
          await fetch(`/api/patients/${patientId}/reports/${created.id}/upload-original`, {
            method: "POST",
            body: formData,
          });
        }
        setLinkSuccess(`Patient mis à jour : ${lastName} ${firstName}`);
        onPatientLinked?.(patientId, false);
      } else {
        const deanonymMap = buildDeanonymizationMap(anonymizationResult.replacements);
        const createRes = await fetch("/api/patients", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName,
            lastName,
            birthDate: extracted.birthDate ?? undefined,
            notes: extracted.notes ?? undefined,
            nameDeclensions: declensions,
            notesAnonymizationMap: Object.keys(deanonymMap).length > 0 ? deanonymMap : undefined,
          }),
        });
        if (!createRes.ok) throw new Error("Erreur création patient");
        const created = await createRes.json();
        const patientId = created.id;

        for (const patho of extracted.pathologies || []) {
          await fetch(`/api/patients/${patientId}/pathologies`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: patho.name, description: patho.description ?? undefined }),
          });
        }
        const reportRes = await fetch(`/api/patients/${patientId}/reports`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: `Rapport importé - ${file?.name ?? new Date().toLocaleDateString("fr-FR")}`,
            content: anonymizationResult.anonymizedText.substring(0, 5000),
            summary: extracted.reportSummary ?? undefined,
            improvements: extracted.reportImprovements ?? undefined,
            actionsToCome: extracted.reportActionsToCome ?? undefined,
            reportDate: new Date().toISOString().split("T")[0],
            anonymizationMap: Object.keys(deanonymMap).length > 0 ? deanonymMap : undefined,
          }),
        });
        if (!reportRes.ok) {
          const errData = await reportRes.json().catch(() => ({}));
          throw new Error(errData?.error ?? "Erreur création du rapport");
        }
        if (file) {
          const created = await reportRes.json();
          const formData = new FormData();
          formData.append("file", file);
          await fetch(`/api/patients/${patientId}/reports/${created.id}/upload-original`, {
            method: "POST",
            body: formData,
          });
        }
        setLinkSuccess(`Nouveau patient créé : ${lastName} ${firstName}`);
        onPatientLinked?.(patientId, true);
      }
      onPatientsRefresh?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'association");
    } finally {
      setIsLinking(false);
    }
  };

  const clearAll = () => {
    setFile(null);
    setExtractionResult(null);
    setIdentifiedPatient(null);
    setAnonymizationResult(null);
    setMatchResult(null);
    setError(null);
    setProgress("");
    setLinkSuccess(null);
  };

  const getDisplayName = () => {
    if (!identifiedPatient?.results?.length) return "";
    const nom = identifiedPatient.results.find(
      (r) => r.format === "NOM" || r.format?.toUpperCase() === "NOM"
    )?.value;
    const prenom = identifiedPatient.results.find(
      (r) =>
        r.format === "Prénom" || r.format === "PRÉNOM" || r.format?.toUpperCase() === "PRENOM"
    )?.value;
    return [nom, prenom].filter(Boolean).join(" ") || "Patient identifié";
  };

  const matchedPatient = matchResult?.matchedPatientId
    ? existingPatients.find((p) => p.id === matchResult.matchedPatientId)
    : null;

  return (
    <div className={cn("space-y-4", className)}>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "relative border-2 border-dashed rounded-2xl p-6 md:p-8 text-center transition-all duration-300",
          isDragging
            ? "border-primary-500 bg-primary-50/50 scale-[1.01]"
            : "border-slate-300 bg-white/40 hover:border-primary-400 hover:bg-white/60",
          isProcessing && "pointer-events-none opacity-80"
        )}
      >
        <input
          type="file"
          accept=".pdf,.doc,.docx,image/*"
          onChange={handleFileSelect}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isProcessing}
        />
        {isProcessing ? (
          <div className="space-y-3">
            <Loader2 className="h-10 w-10 mx-auto text-primary-500 animate-spin" />
            <p className="text-sm font-semibold text-slate-700">{progress}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div
              className={cn(
                "inline-flex p-3 rounded-xl transition-all",
                isDragging ? "bg-primary-100 text-primary-600" : "bg-slate-100 text-slate-500"
              )}
            >
              <Upload className="h-8 w-8" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">
                Déposer un document pour anonymiser et associer à un patient
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Même logique que Bilans : identification IA, anonymisation, recherche par déclinaisons du nom
              </p>
            </div>
            <div className="flex justify-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 rounded-lg bg-primary-100 text-primary-700 text-xs font-medium">
                PDF
              </span>
              <span className="px-2 py-0.5 rounded-lg bg-primary-100 text-primary-700 text-xs font-medium">
                Word
              </span>
              <span className="px-2 py-0.5 rounded-lg bg-primary-100 text-primary-700 text-xs font-medium">
                Images
              </span>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-xl bg-error-50 border border-error-200 px-4 py-3 text-sm text-error-700 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {linkSuccess && (
        <div className="rounded-xl bg-success-50 border border-success-200 px-4 py-3 text-sm text-success-700 flex items-center gap-2">
          <CheckCircle className="h-4 w-4 shrink-0" />
          {linkSuccess}
        </div>
      )}

      {file && !isProcessing && identifiedPatient && anonymizationResult && (
        <div className="rounded-2xl bg-white/60 backdrop-blur-xl border border-white/50 shadow-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200/80 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary-600" />
              <span className="font-semibold text-slate-900">Document anonymisé</span>
            </div>
            <button
              type="button"
              onClick={clearAll}
              className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              aria-label="Effacer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="p-4 space-y-4">
            <p className="text-sm text-slate-700">
              <strong>Patient identifié :</strong> {getDisplayName()}
            </p>
            {/* Variantes et codes : même principe [code] pour l'anonymisation */}
            {identifiedPatient.results.length > 0 && (
              <div className="rounded-xl bg-slate-50 border border-slate-200/80 p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  {identifiedPatient.results.length} variantes → codes [code] dans le document
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {identifiedPatient.results.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg bg-white border border-slate-100"
                    >
                      <span className="text-sm text-slate-800 truncate" title={item.value}>
                        {item.value}
                      </span>
                      {item.anonymousId && (
                        <span className="text-xs font-mono shrink-0 px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
                          [{item.anonymousId}]
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className="text-xs text-slate-500">
                    Correspondance JSON (variante → code) :
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const table = identifiedPatient.results.reduce(
                        (acc, r) => {
                          if (r.value && r.anonymousId) acc[r.value] = r.anonymousId;
                          return acc;
                        },
                        {} as Record<string, string>
                      );
                      navigator.clipboard.writeText(JSON.stringify(table, null, 2));
                    }}
                    className="text-xs px-2 py-1 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300"
                  >
                    Copier JSON
                  </button>
                </div>
              </div>
            )}
            {matchResult && (
              <div className="rounded-xl bg-slate-50 p-4">
                {matchResult.matchedPatientId && matchedPatient ? (
                  <p className="text-sm text-slate-800">
                    Patient existant trouvé :{" "}
                    <strong>
                      {matchedPatient.lastName} {matchedPatient.firstName}
                    </strong>{" "}
                    <span className="text-slate-500">({matchResult.confidence})</span>
                  </p>
                ) : (
                  <p className="text-sm text-slate-800">
                    Aucun patient correspondant → sera créé : <strong>{getDisplayName()}</strong>
                  </p>
                )}
                {matchResult.reason && (
                  <p className="text-xs text-slate-500 mt-1">{matchResult.reason}</p>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={handleLinkToPatient}
              disabled={isLinking}
              className={cn(
                "w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all",
                isLinking
                  ? "bg-primary-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg"
              )}
            >
              {isLinking ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Extraction et association en cours...
                </>
              ) : (
                <>
                  <User className="h-5 w-5" />
                  Extraire infos clés & notes, {matchResult?.matchedPatientId ? "mettre à jour le patient et ajouter le rapport" : "créer le patient et ajouter le rapport"}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
